import type { AxiosError } from "axios";

type RetryFn<T> = () => Promise<T>;

export interface RetryOptions {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  retryableStatusCodes?: number[];
  retryableErrorCodes?: string[];
  onRetry?: (attempt: number, error: unknown, nextDelayMs: number) => void;
}

const defaultStatusCodes = [408, 425, 429, 500, 502, 503, 504];
const defaultErrorCodes = ["ECONNABORTED", "ETIMEDOUT", "EAI_AGAIN"];

const sleep = (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

export async function requestWithRetry<T>(
  fn: RetryFn<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    initialDelayMs = 500,
    maxDelayMs = 4000,
    retryableStatusCodes = defaultStatusCodes,
    retryableErrorCodes = defaultErrorCodes,
    onRetry,
  } = options;

  let attempt = 0;
  let delayMs = initialDelayMs;

  while (attempt < maxAttempts) {
    attempt += 1;
    try {
      return await fn();
    } catch (error) {
      const axiosError = error as AxiosError | undefined;
      const status = axiosError?.response?.status;
      const errorCode = axiosError?.code;
      const isRetryableStatus =
        typeof status === "number" && retryableStatusCodes.includes(status);
      const isRetryableCode =
        typeof errorCode === "string" && retryableErrorCodes.includes(errorCode);

      const canRetry =
        attempt < maxAttempts &&
        (isRetryableStatus || isRetryableCode || (!status && !errorCode));

      if (!canRetry) {
        throw error;
      }

      if (onRetry) {
        onRetry(attempt, error, delayMs);
      }

      await sleep(delayMs);
      delayMs = Math.min(delayMs * 2, maxDelayMs);
    }
  }

  // The loop always returns or throws before reaching this point,
  // but TypeScript expects a value, so throw here as a defensive guard.
  throw new Error("requestWithRetry exhausted without returning a result");
}
